import {
  PROGRESS_ROW_TYPE_MATERIAL_SPEND,
  PROGRESS_ROW_TYPE_SPEND,
  PROGRESS_ROW_TYPE_HOURS,
  PROGRESS_ROW_TYPE_LABOR_COST,
} from './progressConstants';
import {
  PROGRESS_UPDATE_TYPE_NOTES,
  PROGRESS_UPDATE_TYPE_QUANTITY,
  PROGRESS_UPDATE_TYPE_CURRENCY,
  PROGRESS_UPDATE_TYPE_ACTIVITY,
  PROGRESS_UPDATE_FIELD_NOTES,
  PROGRESS_UPDATE_FIELD_QUANTITY,
  PROGRESS_UPDATE_FIELD_CURRENCY,
  PROGRESS_UPDATE_FIELD_ACTIVITY,
  PROGRESS_UPDATE_TYPE_LOGGED_HOURS,
  PROGRESS_UPDATE_FIELD_LOGGED_HOURS,
  PROGRESS_UPDATE_TYPE_INVOICE_ADD,
  PROGRESS_UPDATE_TYPE_INVOICE_UPDATE,
  PROGRESS_UPDATE_TYPE_INVOICE_DELETE,
  PROGRESS_UPDATE_TYPE_UPDATE_COST_ADD,
} from '../history/historyConstants';
import { getPhaseCostcodeKey } from '../../forms/formHelpers';
import { isNullOrUndefined } from '../../helpers/helpers';
import {
  EQUIPMENT_CATEGORY_ID,
  LABOR_CATEGORY_ID,
  MATERIAL_CATEGORY_ID,
  OVERHEAD_CATEGORY_ID,
} from '../../helpers/costcodeHelpers';

export default {};

/**
 * Generates a progress key prefix (without category) for matching multiple categories
 * @param {string} projectId
 * @param {string} phaseId
 * @param {string} costcodeId
 * @param {string} formId
 * @returns {string} progress key prefix
 */
export const getProgressKeyPrefix = ({
  projectId = null,
  phaseId = null,
  costcodeId = null,
  formId = null,
}) => `${projectId}.${phaseId}.${costcodeId}.${formId}.`;

/**
 * Generates a full progress key including category
 * @param {string} projectId
 * @param {string} phaseId
 * @param {string} costcodeId
 * @param {string} formId
 * @param {number} defaultCategoryId
 * @param {string} customCategoryId
 * @returns {string} progress key
 */
export const getProgressKey = ({
  projectId = null,
  phaseId = null,
  costcodeId = null,
  formId = null,
  defaultCategoryId = null,
  customCategoryId = null,
}) => {
  const categoryKey = customCategoryId || defaultCategoryId || 'null';
  const prefixKey = getProgressKeyPrefix({
    projectId, phaseId, costcodeId, formId,
  });
  return `${prefixKey}${categoryKey}`;
};

/**
 * Gets all progress arrays for a costcode (across all categories)
 * and returns them combined
 */
const getProgress = ({
  state,
  projectId,
  phaseId,
  costcodeId,
  formId,
}) => {
  const keyPrefix = getProgressKeyPrefix({
    projectId, phaseId, costcodeId, formId,
  });

  // Find all keys that match this costcode (any category)
  const matchingKeys = Object.keys(state).filter((key) => key.startsWith(keyPrefix));

  // Combine all progress arrays
  const allProgressUpdates = matchingKeys.flatMap((key) => state[key] || []);

  // Sort by timestamp
  allProgressUpdates.sort((a, b) => a.timestamp - b.timestamp);

  return allProgressUpdates;
};

const getProgressTasksArray = ({
  state = {},
  phaseId,
  costcodeId,
}) => {
  const phaseCostcodeKey = getPhaseCostcodeKey(phaseId, costcodeId);
  const {
    [phaseCostcodeKey]: {
      tasks: progressTasksArray = [],
    } = {},
  } = state;
  return progressTasksArray;
};

/**
 * Gets progress history for a specific costcode + category combination
 * @param {object} progressState
 * @param {string} projectId
 * @param {string} phaseId
 * @param {string} costcodeId
 * @param {string} formId
 * @param {number} defaultCategoryId
 * @param {string} customCategoryId
 * @returns {array} progress history for the specific category
 */
export const getProgressForCategory = ({
  progressState,
  projectId,
  phaseId,
  costcodeId,
  formId = null,
  defaultCategoryId,
  customCategoryId,
}) => {
  const key = getProgressKey({
    projectId,
    phaseId,
    costcodeId,
    formId,
    defaultCategoryId,
    customCategoryId,
  });

  return progressState[key] || [];
};

const getLatestProgress = ({
  state,
  projectId,
  phaseId,
  costcodeId,
  formId,
  defaultCategoryId = null,
  customCategoryId = null,
}) => {
  // If category is specified, get category-specific progress
  const progressArray = (defaultCategoryId || customCategoryId)
    ? getProgressForCategory({
      progressState: state,
      projectId,
      phaseId,
      costcodeId,
      formId,
      defaultCategoryId,
      customCategoryId,
    })
    : getProgress({
      state,
      projectId,
      phaseId,
      costcodeId,
      formId,
    });
  const { length } = progressArray;
  const {
    progress = 0,
    quantityUsed = 0,
    actualSpend = 0,
  } = progressArray[length - 1] || {};
  return {
    progress: parseInt(progress * 100, 10),
    quantityUsed,
    actualSpend,
  };
};

export const getProgressFromState = ({
  progressState = {},
  taskState = {},
  type,
  projectId,
  phaseId,
  costcodeId,
  formId,
  children = [],
  defaultCategoryId = null,
  customCategoryId = null,
}) => {
  // Get Progress - use category-specific if provided:
  const progressArray = (defaultCategoryId || customCategoryId)
    ? getProgressForCategory({
      progressState,
      projectId,
      phaseId,
      costcodeId,
      formId,
      defaultCategoryId,
      customCategoryId,
    })
    : getProgress({
      state: progressState, projectId, phaseId, costcodeId, formId,
    });

  // Get Logged Hours (tasks):
  const progressTasksArray = getProgressTasksArray({
    state: taskState, phaseId, costcodeId,
  });

  const {
    progress: latestProgressUpdate,
    actualSpend,
    quantityUsed,
  } = getLatestProgress({
    state: progressState,
    type,
    projectId,
    phaseId,
    costcodeId,
    formId,
    defaultCategoryId,
    customCategoryId,
  });
  let fullChildrenHistory = [];
  if (children.length > 0) {
    // When parent is a phase, don't pass category filters to children
    // Phases aggregate across all categories, so each child should use its own category filter
    const shouldFilterChildren = type !== 'phase';
    children.forEach((child) => {
      let childDefaultCategoryId = null;
      let childCustomCategoryId = null;
      if (!shouldFilterChildren && child.activeCategoryId) {
        const isCustomCategory = typeof child.activeCategoryId === 'string' && child.activeCategoryId.length > 0;
        childDefaultCategoryId = isCustomCategory ? null : child.activeCategoryId;
        childCustomCategoryId = isCustomCategory ? child.activeCategoryId : null;
      }

      const {
        progressHistory: childHistory,
      } = getProgressFromState({
        ...child,
        progressState,
        taskState,
        // Only pass category filters if parent is not a phase
        // Otherwise, let each child use its own activeCategoryId from the child object
        defaultCategoryId: shouldFilterChildren ? defaultCategoryId : childDefaultCategoryId,
        customCategoryId: shouldFilterChildren ? customCategoryId : childCustomCategoryId,
      });
      fullChildrenHistory = fullChildrenHistory.concat(childHistory);
    });
  }

  const fullHistory = progressArray.concat(progressTasksArray, fullChildrenHistory);
  fullHistory.sort((a, b) => b.timestamp - a.timestamp);
  return {
    progressHistory: fullHistory,
    latestProgressUpdate: parseInt(latestProgressUpdate, 10),
    quantityUsed,
    actualSpend,
  };
};

/**
 * Modifies progress history entry details depending on transaction type (spend or quantity):
 * @returns {object}
 */
export const getProgressHistoryEntryDiffValues = ({
  duration = 0,
  type: progressType = '',
  diffSpend = 0,
  diffQuantity = 0,
  lastProgress,
  progress,
  diff,
  actualSpend,
  lastSpend,
  quantityUsed,
  lastQuantity,
  timestamp = 0,
  device,
  files = [],
  noteDescription = '',
  filteredValues,
}) => {
  const isSpendUpdate = diffSpend !== 0;
  const isQuantityUpdate = diffQuantity !== 0;
  const isNotesUpdate = files?.length > 0 || noteDescription;
  const isLoggedHoursUpdate = duration || progressType === 'logged hours';
  const isInvoiceUpdate = progressType === PROGRESS_UPDATE_TYPE_INVOICE_ADD
    || progressType === PROGRESS_UPDATE_TYPE_INVOICE_UPDATE
    || progressType === PROGRESS_UPDATE_TYPE_INVOICE_DELETE;
  const isUpdateCostStep = progressType === PROGRESS_UPDATE_TYPE_UPDATE_COST_ADD;
  let type;
  let field;
  let pastValue = progress;
  let newValue;
  let ourDiff = diff;

  if (isNotesUpdate) {
    type = PROGRESS_UPDATE_TYPE_NOTES;
    field = PROGRESS_UPDATE_FIELD_NOTES;
    newValue = noteDescription;
  } else if (isQuantityUpdate) {
    type = PROGRESS_UPDATE_TYPE_QUANTITY;
    field = PROGRESS_UPDATE_FIELD_QUANTITY;
    newValue = filteredValues?.quantityUsed || quantityUsed;
    pastValue = filteredValues?.lastQuantity || lastQuantity;
    ourDiff = filteredValues?.diffQuantity || diffQuantity;
  } else if (isSpendUpdate || isInvoiceUpdate || isUpdateCostStep) {
    type = (isInvoiceUpdate || isUpdateCostStep)
      ? progressType : PROGRESS_UPDATE_TYPE_CURRENCY;
    field = PROGRESS_UPDATE_FIELD_CURRENCY;
    newValue = filteredValues?.actualSpend || actualSpend;
    pastValue = filteredValues?.lastSpend || lastSpend;
    ourDiff = filteredValues?.diffSpend || diffSpend;
  } else if (isLoggedHoursUpdate) {
    type = PROGRESS_UPDATE_TYPE_LOGGED_HOURS;
    field = PROGRESS_UPDATE_FIELD_LOGGED_HOURS;
  } else {
    type = PROGRESS_UPDATE_TYPE_ACTIVITY;
    field = PROGRESS_UPDATE_FIELD_ACTIVITY;
    newValue = filteredValues?.progress || progress;
    pastValue = filteredValues?.lastProgress || lastProgress;
    ourDiff = filteredValues?.diff || diff;
  }

  return {
    duration,
    pastValue,
    newValue,
    diff: ourDiff,
    timestamp,
    device,
    files,
    type,
    field,
  };
};

/**
 * Dynamically calculates the styling depending on which progress bars are used
 * @param {string} type - One of Material, Labor, Overhead
 * @param {boolean} hasActualSpendProgressBar
 * @param {boolean} hasActualHoursProgressBar
 * @param {boolean} hasActualLaborCostProgressBar
 * @returns {object} object containing styling
 */
export const getProgressRowStyling = ({
  type,
  hasActualHoursProgressBar,
  hasActualLaborCostProgressBar,
}) => {
  const styling = {
    display: 'grid',
    marginRight: 30,
  };

  if (type !== 'Material') { // Labour, overhead, or material
    // All 3 bars
    if (hasActualLaborCostProgressBar && hasActualHoursProgressBar) {
      styling.paddingTop = 0;
      // Activity bar + Actual hours or Actual Labor Cost bar:
    } else if (hasActualHoursProgressBar || hasActualLaborCostProgressBar) {
      styling.paddingTop = 29;
    } else {
    // Activity bar only:
      styling.paddingBottom = 17;
    }
  } else { // Material
    styling.paddingBottom = 22;
  }

  return styling;
};

/**
 * Retrieves the styling for Estimated Hours/Cost Column
 * @param {boolean} useBothEstimates
 * @returns {object} styling
 */
export const getStackedEstimatesStyling = ({
  useBothEstimates,
}) => (useBothEstimates ? { rowGap: 30, paddingBottom: 50 } : {});

/**
 * Retrieves the styling for Actual Hours/Spend Column
 * @param {boolean} useBothEstimates
 * @returns {object} styling
 */
export const getStackedActualStyling = ({
  useBothEstimates,
}) => (useBothEstimates ? { rowGap: 26, paddingBottom: 46 } : {});

/**
 * Computes the current progress depending on the type
 * @param {string} type - progress bar type
 * @param {boolean} displayMode  - whether progress bar allows user input
 * @param {number} estimatedCost
 * @param {number} estimatedHours
 * @param {number} actualSpend
 * @param {number} actualHours
 * @param {number} latestProgressUpdate
 * @param {number} actualLaborCost
 * @returns {number} the current progress
 */
export const computeProgress = ({
  type,
  displayMode,
  estimatedCost,
  estimatedHours,
  actualSpend = 0,
  actualHours = 0,
  latestProgressUpdate,
  actualLaborCost = 0,
}) => {
  if (!displayMode) return latestProgressUpdate;
  switch (type) {
    case PROGRESS_ROW_TYPE_SPEND:
    case PROGRESS_ROW_TYPE_MATERIAL_SPEND:
      if (!estimatedCost) return latestProgressUpdate ?? 0;
      return parseFloat(((actualSpend / estimatedCost) * 100).toFixed(1));
    case PROGRESS_ROW_TYPE_HOURS:
      if (!estimatedHours) return latestProgressUpdate ?? 0;
      return parseFloat(((actualHours / estimatedHours) * 100).toFixed(1));
    case PROGRESS_ROW_TYPE_LABOR_COST:
      if (!estimatedCost) return latestProgressUpdate ?? 0;
      return parseFloat((((actualLaborCost + actualSpend) / estimatedCost) * 100).toFixed(1));
    default:
      return latestProgressUpdate ?? 0;
  }
};

/**
 * Gets the actual cost for a costcode
 * @param {object} costcodeDetailsMap
 * @param {string} costcodeId
 * @param {string} phaseId
 * @param {object} userMap
 * @returns {number} the actual cost
 */
export const getActualCostForCostCode = ({
  costcodeDetailsMap,
  costcodeId,
  phaseId,
  userMap,
}) => {
  if (!costcodeDetailsMap || !costcodeId) return 0;
  const phaseCostcodeKey = getPhaseCostcodeKey(phaseId, costcodeId);
  const { tasks, costcodeTaskSummary } = costcodeDetailsMap[phaseCostcodeKey] || {};

  if (!isNullOrUndefined(costcodeTaskSummary?.totalCost)) {
    return costcodeTaskSummary.totalCost;
  }
  if (!tasks || !tasks.length) return 0;

  return tasks.reduce((acc, task) => {
    const { duration = 0, userId } = task;
    const { wage = 0 } = userMap[userId] || {};
    const cost = duration * wage;
    return acc + cost;
  }, 0);
};

/**
 * Gets the billed amount for a costcode (from time entries)
 * @param {object} costcodeDetailsMap
 * @param {string} costcodeId
 * @param {string} phaseId
 * @returns {number} the billed amount
 */
export const getBilledAmountForCostCode = ({
  costcodeDetailsMap,
  costcodeId,
  phaseId,
}) => {
  if (!costcodeDetailsMap || !costcodeId) return 0;
  const phaseCostcodeKey = getPhaseCostcodeKey(phaseId, costcodeId);
  const { costcodeTaskSummary } = costcodeDetailsMap[phaseCostcodeKey] || {};
  return costcodeTaskSummary?.totalBilling ?? 0;
};

/**
 * Gets the billed amount for a costcode (from cost updates)
 * @param {array} updates array of update metadata
 * @param {string} type costcode type
 */
export const getBilledFromCostUpdates = ({
  updates,
  type,
}) => {
  if (!updates.length) return 0;

  let totalBilling = 0;

  updates.forEach((update) => {
    if (type === 'Material') {
      const { cost = 0, quantity = 0, markup = 0 } = update;
      totalBilling += ((markup * cost) + cost) * quantity;
    } else if (type === 'Equipment') {
      const { dailyBillingRate = 0, hourlyBillingRate = 0, hours = 0 } = update;
      totalBilling += dailyBillingRate || (hourlyBillingRate * hours);
    } else {
      const { total = 0 } = update;
      totalBilling += total;
    }
  });

  return totalBilling;
};

/**
 * Gets and formats the actual hours for a costcode
 * @param {object} costcodeDetailsMap
 * @param {string} costcodeId
 * @param {string} phaseId
 * @returns {string} formatted actual hours label
 */
export const getFormattedHoursLabel = ({
  costcodeDetailsMap,
  costcodeId,
  phaseId,
  useLabel = true,
}) => {
  if (!costcodeDetailsMap || !costcodeId) return '0 h';
  const phaseCostcodeKey = getPhaseCostcodeKey(phaseId, costcodeId);
  const {
    costcodeTaskSummary: {
      totalHours,
    } = {},
  } = costcodeDetailsMap[phaseCostcodeKey] || {};
  if (!useLabel) return totalHours ?? 0;
  return totalHours ? `${totalHours.toFixed(2)} h` : '0 h';
};

/**
 * Gets the actual spend for a costcode (aggregated across all categories)
 * @param {string} costcodeId
 * @param {string} phaseId
 * @param {string} projectId
 * @param {object} progresses
 * @returns {number} actual spend
 */
export const getActualSpend = ({
  costcodeId,
  phaseId,
  projectId,
  progresses,
}) => {
  const keyPrefix = getProgressKeyPrefix({
    projectId,
    phaseId,
    costcodeId,
  });

  // Find all keys that match this costcode (any category)
  const matchingKeys = Object.keys(progresses).filter((key) => key.startsWith(keyPrefix));

  // Sum actualSpend from the latest update of each category
  let totalActualSpend = 0;
  matchingKeys.forEach((key) => {
    const progressArray = progresses[key] || [];
    const { actualSpend = 0 } = progressArray[progressArray.length - 1] || {};
    totalActualSpend += actualSpend;
  });

  return totalActualSpend;
};

/**
 * Gets the actual spend for a specific costcode + category combination
 * @param {string} costcodeId
 * @param {string} phaseId
 * @param {string} projectId
 * @param {object} progresses
 * @param {number} defaultCategoryId
 * @param {string} customCategoryId
 * @returns {number} actual spend for the specific category
 */
export const getActualSpendForCategory = ({
  costcodeId,
  phaseId,
  projectId,
  progresses,
  defaultCategoryId,
  customCategoryId,
}) => {
  const key = getProgressKey({
    projectId,
    phaseId,
    costcodeId,
    defaultCategoryId,
    customCategoryId,
  });

  const progressArray = progresses[key] || [];
  const { actualSpend = 0 } = progressArray[progressArray.length - 1] || {};

  return actualSpend;
};

/**
 * Computes the total actual spend for a project
 * @param {array} costcodeList - Array of costcodes
 * @param {object} costcodeDetailsMap - Costcode details map
 * @param {object} userMap - User map for overhead and labor calculations
 * @param {string} projectId - Project ID
 * @param {object} progresses - Progress state data
 * @param {number} estimatedBurdenRate - Estimated burden rate for labor calculations
 * @param {object} categoriesIdMap - Map of category objects by ID
 * @returns {number} The total actual spend of costcodes in the project
 */
const getCostcodesActualTotalSpend = ({
  costcodeList,
  costcodeDetailsMap,
  userMap,
  projectId,
  progresses,
  estimatedBurdenRate,
  categoriesIdMap,
}) => {
  let actualTotalSpend = 0;

  costcodeList.forEach((costcode) => {
    const { categoryIds = [], id: costcodeId, phaseId } = costcode;
    const actualCost = getActualCostForCostCode({
      costcodeDetailsMap,
      costcodeId,
      phaseId,
      userMap,
    });

    categoryIds.forEach((categoryId) => {
      const actualSpend = getActualSpendForCategory({
        costcodeId,
        phaseId,
        projectId,
        progresses,
        defaultCategoryId: categoryId,
      });
      if (categoryId === LABOR_CATEGORY_ID) {
        const actualBurdenCost = actualCost * estimatedBurdenRate;
        const totalLaborCost = actualCost + actualSpend;
        actualTotalSpend += (totalLaborCost + actualBurdenCost);
      } else if (
        categoryId === OVERHEAD_CATEGORY_ID
        || categoriesIdMap?.[categoryId]?.allowTimeTracking // custom category w/ time tracking
      ) {
        const totalCost = actualCost + actualSpend;
        actualTotalSpend += totalCost;
      } else { // material or equipment or any other custom category
        actualTotalSpend += actualSpend;
      }
    });
  });

  return actualTotalSpend;
};

/**
 * Computes the total actual spend for a project
 * @param {array} phases - Array of project phases
 * @param {array} costcodes - Array of project costcodes
 * @param {string} projectId - Project ID
 * @param {object} progresses - Progress state data
 * @param {object} projectCostcodeDetailsMap - Costcode details map
 * @param {object} userMap - User map for labor calculations
 * @param {array} filteredSubContracts - Filtered subcontract data
 * @param {array} uncategorizedInvoices - Uncategorized invoice data
 * @param {array} uncategorizedMaterialUpdates - Uncategorized material updates
 * @param {array} uncategorizedEquipmentUpdates - Uncategorized equipment updates
 * @param {number} estimatedBurdenRate - Estimated burden rate for labor calculations
 * @param {object} categoriesIdMap - Map of category objects by ID
 * @returns {number} The total actual spend for the project
 */
export const computeActualTotalSpend = ({
  phases = [],
  costcodes = [],
  projectId,
  progresses = {},
  projectCostcodeDetailsMap = {},
  userMap = {},
  filteredSubContracts = [],
  uncategorizedInvoices = [],
  uncategorizedMaterialUpdates = [],
  uncategorizedEquipmentUpdates = [],
  estimatedBurdenRate = 0,
  categoriesIdMap = {},
}) => {
  let actualTotalSpend = 0;

  // Process phased costcodes
  const phasedCostcodes = [];
  phases.forEach((phase) => {
    const { id: phaseId, projectId: phaseProjectId, costcodeId } = phase;
    if (phaseProjectId !== projectId || !costcodeId) return;

    const costcode = costcodes.find((cc) => cc.id === costcodeId);
    if (costcode) {
      phasedCostcodes.push({
        ...costcode,
        phaseId,
      });
    }
  });

  // Process unphased costcodes
  const unphasedCostcodes = costcodes.filter(
    ({ projectId: costcodeProjectId, phased }) => (
      costcodeProjectId === projectId && !phased
    ),
  );

  // Process all costcodes
  const costcodesActualTotalSpend = getCostcodesActualTotalSpend({
    costcodeList: [...phasedCostcodes, ...unphasedCostcodes],
    costcodeDetailsMap: projectCostcodeDetailsMap,
    projectId,
    progresses,
    userMap,
    estimatedBurdenRate,
    categoriesIdMap,
  });

  actualTotalSpend += costcodesActualTotalSpend;

  // Add subcontract costs
  filteredSubContracts.forEach(({ costToDate }) => {
    actualTotalSpend += costToDate;
  });

  // Add uncategorized costs
  // HARBOUR-8265: Include both undistributed amounts AND distributions without categories
  const uncategorizedCosts = uncategorizedInvoices.reduce((costs, invoice) => {
    const undistributedAmount = invoice.amount - invoice.amountDistributed;
    const needsCategoryAmount = invoice.uncategorizedDistributionAmount || 0;
    return costs + undistributedAmount + needsCategoryAmount;
  }, 0);
  const uncategorizedMaterialCosts = uncategorizedMaterialUpdates.reduce((costs, update) => (
    costs + (update.total || 0)
  ), 0);
  const uncategorizedEquipmentCosts = uncategorizedEquipmentUpdates.reduce((costs, update) => (
    costs + (update.total || 0)
  ), 0);

  actualTotalSpend += uncategorizedCosts;
  actualTotalSpend += uncategorizedMaterialCosts;
  actualTotalSpend += uncategorizedEquipmentCosts;

  return actualTotalSpend;
};

/**
 * Process uncategorized invoices by filtering and formatting them
 * An invoice is considered uncategorized if:
 * 1. The distributed amount is less than the invoice total (not fully distributed), OR
 * 2. Any distribution has no category assigned (needs category assignment)
 * @param {Array} invoices - Array of invoice objects
 * @param {Array} statusMap - Map of status objects
 * @param {Array} vendors - Array of vendor objects
 * @param {string} projectId - Project ID
 * @returns {Array} Formatted uncategorized invoices
 */
export const processUncategorizedInvoices = ({
  invoices = [],
  statusMap = {},
  vendors = {},
  projectId = null,
}) => {
  let projectInvoices = invoices;

  // Filter by projects if provided
  if (projectId) {
    projectInvoices = invoices.filter((i) => (
      i.projectId === projectId
      || i.costcodeDistributions?.some(({ projectId: distProjectId }) => (
        distProjectId === projectId
      ))
    ));
  }

  return projectInvoices.map((invoice) => {
    const { vendorId, statusId, costcodeDistributions = [] } = invoice ?? {};

    const { title: statusName } = statusMap[statusId] ?? {};
    const { name: vendorName } = vendors[vendorId] ?? {};

    const amountDistributed = costcodeDistributions.reduce((total, distribution) => (
      total + distribution.amount
    ), 0);

    // HARBOUR-8265: Check for distributions without a category assigned
    // These need manual category assignment by the user
    const uncategorizedDistributionAmount = costcodeDistributions
      .filter((d) => !d.defaultCategoryId && !d.customCategoryId)
      .reduce((total, d) => total + d.amount, 0);

    return {
      ...invoice,
      amountDistributed,
      uncategorizedDistributionAmount,
      vendor: vendorName,
      status: statusName,
    };
  }).filter(({ amount, amountDistributed, uncategorizedDistributionAmount }) => (
    // Invoice is uncategorized if not fully distributed OR has distributions without categories
    amountDistributed < amount || uncategorizedDistributionAmount > 0
  ));
};

/**
 * Process uncategorized material and equipment updates
 * @param {Array} projectCostUpdates - Array of project cost update objects
 * @param {object} [options]
 * @param {boolean} [options.isBudgetUpdate=false] - When true, only includes budget updates
 * @returns {Object} Object containing uncategorized material and equipment updates
 */
export const processUncategorizedUpdates = (
  projectCostUpdates = [],
  { isBudgetUpdate = false } = {},
) => {
  const equipmentUpdates = [];
  const materialUpdates = [];

  const filteredUpdates = projectCostUpdates.filter(
    (update) => !update.costcodeId && !!update.isBudgetUpdate === isBudgetUpdate,
  );

  filteredUpdates.forEach((update) => {
    if (update.type === 'material') {
      materialUpdates.push(update);
    } else {
      equipmentUpdates.push(update);
    }
  });

  return {
    uncategorizedMaterialUpdates: materialUpdates,
    uncategorizedEquipmentUpdates: equipmentUpdates,
  };
};

/**
 * Creates category-specific costcode rows for multi-category costcodes
 * @param {object} params - Parameters object
 * @param {object} params.costcode - Base costcode object
 * @param {array} params.categoryIds - Array of category IDs
 * @param {object} params.categoriesIdMap - Map of category objects by ID
 * @param {function} params.formatCC - Function to format costcode
 * @param {string} params.costcodeName - Name of the costcode (for displayName)
 * @param {string} [params.phaseId] - Optional phase ID
 * @param {object} [params.additionalProps] - Optional additional properties to merge into costcode
 * @returns {array} Array of formatted costcode objects
 */
export const createCategorySpecificCostcodes = ({
  costcode,
  categoryIds,
  categoriesIdMap,
  formatCC,
  costcodeName,
  phaseId = null,
  additionalProps = {},
}) => {
  const isMultiCategory = categoryIds.length > 1;

  return categoryIds.map((categoryId) => {
    const category = categoriesIdMap[categoryId];
    const categoryName = category?.name;

    // For multi-category costcodes, create a category-specific row
    if (isMultiCategory) {
      return formatCC({
        costcode: {
          ...costcode,
          ...additionalProps,
          categoryIds: [categoryId], // override to only show this category
          displayName: `${costcodeName} (${categoryName})`,
        },
        ...(phaseId && { phaseId }),
        categoryId,
      });
    }

    // For single-category costcodes, use the original costcode
    return formatCC({
      costcode: {
        ...costcode,
        ...additionalProps,
      },
      ...(phaseId && { phaseId }),
      categoryId: categoryIds[0],
    });
  });
};

/**
 * Checks if two filters are equal
 * @param {object} filters1 - First filters object
 * @param {object} filters2 - Second filters object
 * @returns {boolean} true if the filters are equal, false otherwise
 */
export const areFiltersEqual = (filters1, filters2) => {
  if (filters1 === filters2) return true;
  if (!filters1 || !filters2) return false;

  const {
    start: startDate1,
    end: endDate1,
  } = filters1 || {};

  const {
    start: startDate2,
    end: endDate2,
  } = filters2 || {};

  if (startDate1 !== startDate2) return false;
  if (endDate1 !== endDate2) return false;

  return true;
};

/**
 * Resolves default/custom category columns to a single map key segment.
 */
const getCostUpdateCategoryKey = (defaultCategoryId, customCategoryId) => (
  customCategoryId || defaultCategoryId || 'null'
);

/**
 * Builds the lookup key for categorized cost updates on a table row or update record.
 */
export const getCategorizedCostUpdateMapKey = ({
  phaseId,
  costcodeId,
  categoryId,
  defaultCategoryId,
  customCategoryId,
}) => {
  const resolvedCategoryId = categoryId ?? getCostUpdateCategoryKey(
    defaultCategoryId,
    customCategoryId,
  );
  return `${phaseId ?? 'unphased'}.${costcodeId}.${resolvedCategoryId}`;
};

/**
 * Groups cost updates by phase, costcode, and category.
 * @param {array} updates
 * @param {object} options
 * @param {boolean} [options.isBudgetUpdate] - When set, only includes matching budget/progress updates
 */
export const buildCategorizedCostUpdateMap = (updates = [], { isBudgetUpdate } = {}) => (
  updates.reduce((acc, update) => {
    if (!update.costcodeId) return acc;
    if (isBudgetUpdate !== undefined && !!update.isBudgetUpdate !== isBudgetUpdate) return acc;

    const key = getCategorizedCostUpdateMapKey({
      phaseId: update.phaseId,
      costcodeId: update.costcodeId,
      defaultCategoryId: update.defaultCategoryId,
      customCategoryId: update.customCategoryId,
    });

    if (!(key in acc)) {
      acc[key] = [];
    }
    acc[key].push(update);
    return acc;
  }, {})
);

/**
 * Attaches category-scoped cost updates to a budget-tracking table row.
 * Parent and phase rows are left unchanged.
 */
export const attachCostUpdatesToRow = (row, map) => {
  if (row.isParentRow || row.isPhaseRow) return row;

  const key = getCategorizedCostUpdateMapKey({
    phaseId: row.phaseId,
    costcodeId: row.id,
    categoryId: row.categoryId,
  });

  return { ...row, costUpdates: map[key] ?? [] };
};

/**
 * Attaches category-scoped cost updates to each row in a budget-tracking table tree.
 */
export const enrichRowsWithCostUpdates = (rows, map) => rows.map((row) => {
  if (row.isParentRow && row.children?.length) {
    return {
      ...row,
      children: row.children.map((child) => attachCostUpdatesToRow(child, map)),
    };
  }
  return attachCostUpdatesToRow(row, map);
});

export const showCostUpdatesIcon = (record) => (
  !record?.isParentRow
  && !record?.isPhaseRow
  && (record?.costUpdates?.length > 0)
);

/**
 * Adds subcontract actuals and estimates onto a cost summary.
 * When no subcontracts exist, the high-level subcontract estimate is used
 * @param {Object} options
 * @param {Object} options.summary - Mutable summary totals
 * @param {Array} options.subcontracts - Subcontracts with costToDate and contractTotal
 * @param {number} options.estimatedSubContractTotalValue - High-level subcontract estimate
 */
export const applySubcontractSummary = ({
  summary,
  subcontracts = [],
  estimatedSubContractTotalValue = 0,
}) => {
  /* eslint-disable no-param-reassign */
  if (!subcontracts?.length) {
    const highLevelEstimate = Number(estimatedSubContractTotalValue) || 0;
    summary.estimatedSubcontractCost += highLevelEstimate;
    summary.estimatedTotalCost += highLevelEstimate;
    return;
  }

  subcontracts.forEach(({
    costToDate,
    contractTotal,
  }) => {
    summary.actualSubcontractSpend += costToDate;
    summary.estimatedSubcontractCost += contractTotal;
    summary.estimatedTotalCost += contractTotal;
  });
  /* eslint-enable no-param-reassign */
};
